// frontend/src/components/dashboard/pos/components/ProductsSection.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Button,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Search,
  Category,
  Coffee,
  Cake,
  LocalDrink,
  MenuBook,
  Error as ErrorIcon,
  LocalDrink as LocalDrinkIcon
} from '@mui/icons-material';
import FastFoodIcon from '@mui/icons-material/Fastfood';
import { motion } from 'framer-motion';
import { commonStyles } from '../styles/posStyles';
import ProductCard from './ProductCard';


const ProductsSection = ({
  categories,
  products,
  selectedCategory,
  searchTerm,
  onCategoryChange,
  onSearchChange,
  onAddToCart,
  loading,
  error,
  isOnline,
  isMobile,
  isTablet,
  settings
}) => {
  const { t } = useTranslation();
  const MotionDiv = motion.div;

  // Category icons mapping
  const categoryIcons = {
    'Food': <FastFoodIcon />,
    'Drinks': <LocalDrink />,
    'Desserts': <Cake />,
    'Coffee': <Coffee />,
    'Combo Meals': <MenuBook />
  };

  // Get category icon
  const getCategoryIcon = (categoryName) => {
    return categoryIcons[categoryName] || <Category />;
  };

  // Get responsive grid columns
  const getGridColumns = () => {
    if (isMobile) {
      return { xs: 6, sm: 4 }; // 2 columns on mobile, 3 on small tablet
    }
    if (isTablet) {
      return { xs: 4, sm: 3, md: 3 }; // 3-4 columns on tablets
    }
    return { xs: 6, sm: 4, md: 3, lg: 2.4, xl: 2 }; // More columns on desktop
  };

  const gridColumns = getGridColumns();

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: { xs: 1, sm: 2 },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minWidth: 0
    }}>
      {/* Search Bar */}
      <Box sx={{ mb: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('pos.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }
          }}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {/* Mobile Categories Tabs */}
      <Box sx={{ 
        display: { xs: 'block', md: 'none' }, 
        mb: { xs: 1.5, sm: 2 }, 
        flexShrink: 0 
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Tabs
            value={selectedCategory === 'all' ? 'all' : (selectedCategory ? selectedCategory.toString() : 'all')}
            onChange={(_, value) => onCategoryChange(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': { 
                minWidth: { xs: 80, sm: 100 },
                textTransform: 'none',
                py: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.8rem', sm: '0.9rem' }
              },
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { opacity: 0.3 }
              }
            }}
          >
            <Tab 
              icon={<Category />} 
              label={t('pos.allCategories')} 
              value="all"
              iconPosition="start"
            />
            {Array.isArray(categories) && categories
              .filter(category => category && (category.category_id || category.id)) // Filter out invalid items
              .map((category) => {
                // SAFE: Get category properties with fallbacks
                const categoryId = category.category_id || category.id;
                const categoryName = category.name || category.category_name || 'Unknown Category';
                
                // SAFE: Ensure categoryId can be converted to string
                const categoryIdString = categoryId ? categoryId.toString() : '';

                return (
                  <Tab
                    key={categoryId}
                    icon={getCategoryIcon(categoryName)}
                    label={categoryName}
                    value={categoryIdString}
                    iconPosition="start"
                  />
                );
              })}
          </Tabs>
        )}
      </Box>
      
      {/* Products Grid */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        pb: { xs: 1, sm: 2 },
        ...commonStyles.scrollbar
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            minHeight: 200
          }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            minHeight: 200,
            p: 3,
            textAlign: 'center'
          }}>
            <ErrorIcon sx={{ 
              fontSize: { xs: 48, sm: 60 }, 
              color: 'error.main', 
              mb: 2, 
              opacity: 0.7 
            }} />
            <Typography variant="h6" gutterBottom color="error" sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}>
              {t('pos.loadFailedTitle')}
            </Typography>
            <Typography color="text.secondary" paragraph sx={{
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
              disabled={!isOnline}
            >
              {t('pos.retry')}
            </Button>
          </Box>
        ) : products.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            minHeight: 200,
            p: 3,
            textAlign: 'center'
          }}>
            <LocalDrinkIcon sx={{ 
              fontSize: { xs: 48, sm: 60 }, 
              color: 'primary.main', 
              mb: 2, 
              opacity: 0.7 
            }} />
            <Typography variant="h6" gutterBottom sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}>
              {t('pos.noProductsFound')}
            </Typography>
            <Typography color="text.secondary" sx={{
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}>
              {t('pos.noProductsFoundDesc')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
            {products.map((product) => (
              <Grid item {...gridColumns} key={product.id}>
                <ProductCard 
                  product={product}
                  onAddToCart={onAddToCart}
                  settings={settings} // Pass settings from props
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default ProductsSection;